import { loadStateOf, progressText, stalledLine } from "../zone-checking.js";
import type { PulseState } from "../../platform/relay-pulse.js";
import type { ZoneLoadState, ZoneLoadProgress } from "../../app/zone-load-gate.js";
import {
  editorialStatusBlock,
  hairlineRow,
  severityTicks,
  iconButton,
  modal,
} from "../../design/components.js";
import { type IncidentView, type StatusLevel } from "./incident-model.js";
import { isFaded, isWorsening } from "./incident-presentation.js";
import { groupIncidents, type AlertGroup } from "./alert-grouping.js";
import { hazardIcon } from "./hazard-icons.js";
import { placesStatus, type MonitoredRing } from "../../platform/monitored-places.js";
import { mountPaperMap, type MapHandle } from "./map-view.js";
import { reportSheet } from "./report-sheet.js";
import type { ReportInput } from "./report-publish.js";
import type { HomePlace } from "../../platform/store.js";
import { DEFAULT_MAP_CENTER } from "../../map/tile-source.js";
import { cellToLatLng, latLngToCell } from "h3-js";
import { cellDistanceKm } from "./incident-model.js";

export interface NowTabCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  home?: HomePlace;
  incidents: IncidentView[];
  simulation: boolean;
  /** Display-clock "now" (ms) — drives E9 stale-fade thresholds. Sim-aware in app.ts. */
  nowMs: number;
  /** Publish a report (wired to the real client in app.ts). Optional so read-only callers still work. */
  onReport?: (input: ReportInput) => Promise<void>;
  /** Optional GPS resolver (res-8 cell or null). */
  geolocate?: () => Promise<string | null>;
  /** Open IncidentDetail for a subject (C4). */
  onOpenIncident?: (subject: string) => void;
  /** Wire a freshly-opened modal into the app's back-button stack (Android/browser back
   *  closes it). Given the modal's raw teardown, returns the close() to use as onClose;
   *  absent (tests/standalone) → the raw teardown is used directly. */
  onModalBack?: (close: () => void) => () => void;
  /** Called with the map center (lat, lng) on mount and after each pan/zoom
   *  settle, so the app subscribes to the firehose for the viewed area.
   *  Only used in the self-mount fallback; the persistent map (mapMount/mapHandle)
   *  is driven by NowMapController, which owns the viewport seeding. */
  onMapCenter?: (lat: number, lng: number) => void;
  /** Monitored rings (home + saved + live) for the union verdict. Falls back to [home] when absent. */
  rings?: MonitoredRing[];
  /** Primary-zone (home+live) status for the headline + primary activity list. When
   *  absent the tab computes it from `rings` (keeps standalone/test callers working). */
  status?: { level: StatusLevel; nearest?: IncidentView; inRadius: Array<{ view: IncidentView; distanceKm: number }> };
  /** Secondary-zone (saved) incidents for the "Other watched places" list. */
  watched?: Array<{ view: IncidentView; distanceKm: number; zoneName?: string }>;
  /** Persistent map owned by the app shell (NowMapController) so the WebGL map
   *  survives re-renders instead of being recreated each render. When both are
   *  provided the tab reuses them; otherwise it self-mounts (tests / direct use). */
  mapMount?: HTMLElement;
  mapHandle?: Pick<MapHandle, "setPlaces" | "setIncidents" | "setLivePosition" | "setFollow">;
  /** Live position (from watchPosition) → draws the travel ring + marker. */
  live?: { lat: number; lng: number; radiusKm: number } | null;
  /** Current camera mode — drives the single camera toggle FAB.
   *  follow = centering on live; fit = framed all zones; free = manually panned. */
  cameraMode?: "follow" | "fit" | "free";
  /** Switch the camera to follow (center on live) — wired to mapHandle.setFollow(true). */
  onRecenter?: () => void;
  /** Network pulse (relay /metrics): is the firehose moving right now? */
  network?: PulseState;
  /** Your own subscriptions: when the last event for your cells arrived, and which feeds
   *  stand behind the incidents currently listed. */
  area?: { lastEventMs?: number; feeds: string[] };
  /** Live position outside every watched ring → "add here as a zone?" row; receives the
   *  live fix's res-8 cell. Absent ⇒ no suggestion. */
  onAddCurrentZone?: (cell: string) => void;
  /** Switch the camera to fit all monitored places + live. */
  onFitAll?: () => void;
  /** Raw reports in view (power-user opt-in) — a capped recent list with each report's
   *  hazard + response type, tappable to open its own detail. Absent ⇒ section hidden. */
  rawReports?: import("./raw-report-model.js").RawReportPoint[];
  onOpenReport?: (id: string) => void;
  /** Whether the incident read-model seed has completed (or timed out). When explicitly
   *  `false`, the Now tab shows "Checking your area…" instead of "All clear" to avoid
   *  a false-negative safety signal. Callers that don't pass this field get the previous
   *  behaviour (undefined is not === false, so the check branch is skipped). */
  incidentsLoaded?: boolean;
  /** Tri-state load state; wins over incidentsLoaded when present. `stalled` = the incident
   *  read model has not answered (retries exhausted / timeout): say "still trying", never all-clear. */
  loadState?: ZoneLoadState;
  /** What the checking state is doing (seed attempt n/max); shown as the subline. */
  loadProgress?: ZoneLoadProgress;
  /** Manual retry once stalled (renders one Retry button under the headline). */
  onRetry?: () => void;
}

const RAW_LIST_CAP = 12;

function applyAgingAttrs(rowWrap: HTMLElement, view: IncidentView, nowMs: number, t: NowTabCtx["t"]): void {
  if (isWorsening(view)) {
    rowWrap.setAttribute("data-worsening", "");
    const badge = document.createElement("span");
    badge.className = "now-activity__badge";
    badge.textContent = t("now.activity.worsening");
    rowWrap.append(badge);
  } else if (isFaded(view, nowMs)) {
    rowWrap.setAttribute("data-stale", "");
  }
}

/** A collapsed card for a group of CAP warning fragments: hazard + area count,
 *  disclosure toggle revealing one tap-to-open row per member. `showTicks`
 *  matches the home list's severity-tick treatment; the watched list omits them. */
function groupCard(
  group: AlertGroup,
  o: { t: NowTabCtx["t"]; nowMs: number; showTicks: boolean; onOpen?: (subject: string) => void },
): HTMLElement {
  const t = o.t;
  const rep = group.members[0];
  const rowWrap = document.createElement("div");
  rowWrap.className = "now-activity__row";
  rowWrap.setAttribute("data-group", group.key);
  applyAgingAttrs(rowWrap, rep.view, o.nowMs, t);

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "now-activity__open";
  toggle.setAttribute("data-group-toggle", group.key);
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-label", t("now.group.expand"));
  toggle.append(
    hairlineRow({
      icon: hazardIcon(group.hazard),
      leading: t(`hazard.${group.hazard}`),
      trailing: t("now.group.areas", { n: group.members.length }),
      tone: "accent",
    }),
  );
  rowWrap.append(toggle);
  if (o.showTicks) rowWrap.append(severityTicks(Math.max(1, Math.round(rep.view.confidence * 5))));

  const members = document.createElement("div");
  members.className = "now-activity__members";
  members.hidden = true;
  for (const m of group.members) {
    const mb = document.createElement("button");
    mb.type = "button";
    mb.className = "now-activity__open now-activity__member";
    mb.setAttribute("data-open-incident", m.view.subject);
    mb.append(
      hairlineRow({
        icon: hazardIcon(m.view.hazard),
        leading: t(`hazard.${m.view.hazard}`),
        ugc: m.zoneName,
        trailing: t("you.loc.km", { n: m.distanceKm.toFixed(1) }),
      }),
    );
    if (o.onOpen) mb.addEventListener("click", () => o.onOpen!(m.view.subject));
    members.append(mb);
  }
  rowWrap.append(members);

  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", open ? "false" : "true");
    members.hidden = open;
  });
  return rowWrap;
}

const HAZARD_HEADLINE: Record<string, string> = {
  fire: "now.status.nearby.fire",
  flood: "now.status.nearby.flood",
  earthquake: "now.status.nearby.earthquake",
  conflict: "now.status.nearby.conflict",
  other: "now.status.nearby.other",
};

/** Now tab — READ-ONLY (C2). Status block + community strip + PaperMap + activity list.
 *  The persistent Report button is present but inert (report flow is C3). */
export function nowTab(ctx: NowTabCtx): HTMLElement {
  const root = document.createElement("section");
  root.className = "screen now-tab";
  const t = ctx.t;
  const home = ctx.home ?? { cell: "", radiusKm: 0 };
  const rings: MonitoredRing[] = ctx.rings ?? (home.cell ? [{ kind: "home", cell: home.cell, radiusKm: home.radiusKm }] : []);
  const status = ctx.status ?? (rings.length ? placesStatus(ctx.incidents, rings, 0) : { level: "allClear" as const, inRadius: [] });

  // --- Status block ---
  let headline: string;
  let subline: string;
  const loadState = loadStateOf(ctx.loadState, ctx.incidentsLoaded);
  if (loadState === "checking") {
    headline = t("zone.checking");
    subline = ctx.loadProgress ? progressText(t, ctx.loadProgress) : "";
  } else if (loadState === "stalled") {
    headline = t("zone.stalled");
    subline = "";
  } else if (status.level === "nearby" && status.nearest) {
    headline = t(HAZARD_HEADLINE[status.nearest.hazard] ?? "now.status.nearby.other");
    const dist = status.inRadius[0]?.distanceKm ?? 0;
    subline = t("now.status.sub", { distance: dist.toFixed(1), place: home.placeName ?? t("now.place.default") });
  } else if (status.level === "stayAware") {
    headline = t("now.status.stayAware");
    subline = t("now.status.sub.allClear");
  } else {
    headline = t("now.status.allClear");
    subline = t("now.status.sub.allClear");
  }
  const baseMeta = t("now.meta", {
    place: home.placeName ?? t("now.place.default"),
    radius: home.radiusKm,
  });
  // Liveness folded into the meta line: "your area · 5 km · 1 min ago · net 5 min".
  const ago = (ms: number | undefined): string => {
    if (ms === undefined) return t("now.meta.none");
    const m = Math.max(0, Math.round((ctx.nowMs - ms) / 60_000));
    return m < 1 ? t("now.meta.justNow") : t("now.meta.ago", { m });
  };
  const netAgo = ctx.network
    ? ctx.network.kind === "unreachable" ? undefined
      : ctx.network.ageS < 60 ? t("now.meta.justNow") : t("now.meta.ago", { m: Math.round(ctx.network.ageS / 60) })
    : undefined;
  const liveness = ctx.network && ctx.area;
  const meta = liveness
    ? `${baseMeta} · ${ago(ctx.area!.lastEventMs)} · ${netAgo !== undefined ? t("now.meta.net", { v: netAgo }) : t("now.meta.netDown")}`
    : baseMeta;
  const statusEl = editorialStatusBlock({ microlabel: meta, headline, subline });
  if (liveness) {
    // The meta line is a link: tap → a modal explaining the (live) values.
    const label = statusEl.querySelector<HTMLElement>(".bx-microlabel")!;
    const link = document.createElement("a");
    link.href = "#";
    link.className = "now-meta-link";
    link.setAttribute("data-action", "area-info");
    link.textContent = meta;
    label.replaceChildren(link);
    link.addEventListener("click", (e) => {
      e.preventDefault();
      if (root.querySelector("[data-sheet=area-info]")) return;
      const content = document.createElement("div");
      content.className = "now-meta-info";
      const lines = [
        t("now.meta.info.radius", { radius: home.radiusKm }),
        t("now.meta.info.area", { area: ago(ctx.area!.lastEventMs) }),
        t("now.meta.info.net", { net: netAgo ?? t("now.meta.netDown") }),
      ];
      const feeds = ctx.area!.feeds.map((f) => t(`incident.source.${f}`)).join(" · ");
      if (feeds) lines.push(t("now.meta.info.feeds", { feeds }));
      for (const text of lines) {
        const pEl = document.createElement("p");
        pEl.className = "bx-body";
        pEl.textContent = text;
        content.append(pEl);
      }
      let sheet: HTMLElement;
      sheet = modal({ title: t("now.meta.info.title"), content, onClose: () => sheet.remove(), sheetId: "area-info" });
      root.append(sheet);
    });
  }
  if (loadState === "stalled" && ctx.onRetry) {
    // One Retry under the "still trying" headline; the gate ignores repeated taps.
    statusEl.append(stalledLine(t, ctx.onRetry).querySelector("[data-action=zone-retry]")!);
  }
  root.append(statusEl);
  // Away from every watched ring while following live → offer to add "here" as a zone.
  if (ctx.live && ctx.onAddCurrentZone) {
    const liveCell = latLngToCell(ctx.live.lat, ctx.live.lng, 8);
    const covered = rings.some((r) => r.kind !== "live" && r.cell && cellDistanceKm(r.cell, liveCell) <= r.radiusKm);
    if (!covered) {
      const onAdd = ctx.onAddCurrentZone;
      const row = document.createElement("button");
      row.type = "button";
      row.className = "now-zone-suggest";
      row.setAttribute("data-action", "add-current-zone");
      row.append(hairlineRow({ leading: t("now.zone.addHere"), trailing: "›", tone: "aid" }));
      row.addEventListener("click", () => onAdd(liveCell));
      root.append(row);
    }
  }
  // The safety headline stays on one line via CSS (nowrap + a vw-scaled clamp); no
  // post-paint JS shrink, which flashed large→small on every background re-render.

  // The community vitals strip (neighbors watching / helpers / median response) was
  // removed from Now: the counts are placeholder dashes until live aggregation (D2),
  // and the row cost map estate. Those vitals belong on a landing/about page once real.
  // metricsStrip itself stays in use on incident/request detail.

  // --- Map (WebGL-guarded; tile-less paper placeholder in tests/degraded clients) ---
  // Mounts for everyone, including browse-only users (no home): without a home
  // it opens on the Iberia overview so panning still subscribes the viewed area.
  let h: Pick<MapHandle, "setPlaces" | "setIncidents" | "setLivePosition" | "setFollow">;
  let mapEl: HTMLElement;
  if (ctx.mapMount && ctx.mapHandle) {
    // Persistent map owned by the app shell (NowMapController). Reused across
    // re-renders so it survives the cold-start incident burst and keeps the home
    // marker/radius drawn (the per-render remount otherwise lost them on reload).
    root.append(ctx.mapMount);
    h = ctx.mapHandle;
    mapEl = ctx.mapMount;
  } else {
    const mapMount = document.createElement("div");
    mapMount.className = "now-map";
    mapMount.setAttribute("data-map", "");
    mapEl = mapMount;
    h = mountPaperMap(mapMount, { centerCell: home.cell || undefined, homeMarker: true, onMoveEnd: ctx.onMapCenter });
    // Seed the first viewport subscription from the initial center. This runs
    // synchronously before the map's async 'moveend' handler is ever registered,
    // so it is not redundant with the first real moveend — it's the only way to
    // capture the initial position for browse-only users or users who don't pan.
    if (ctx.onMapCenter) {
      const [clng, clat] = DEFAULT_MAP_CENTER;
      const [lat, lng] = home.cell ? cellToLatLng(home.cell) : [clat, clng];
      ctx.onMapCenter(lat, lng);
    }
    root.append(mapMount);
  }
  const staticRings = rings.filter((r) => r.kind !== "live").map((r) => ({ cell: r.cell, radiusKm: r.radiusKm }));
  if (h.setPlaces) h.setPlaces(staticRings);
  h.setIncidents(ctx.incidents, ctx.nowMs);
  // Guarded: persistent-map callers pass a partial handle (and some tests use a
  // mapHandle double) that may predate setLivePosition — never assume it exists.
  if (h.setLivePosition) h.setLivePosition(ctx.live ?? null);

  // --- Activity list ---
  const list = document.createElement("div");
  list.className = "now-activity";
  const heading = document.createElement("div");
  heading.className = "bx-microlabel";
  heading.textContent = t("now.activity.title");
  list.append(heading);
  if (status.inRadius.length === 0) {
    const empty = document.createElement("p");
    empty.className = "bx-body";
    empty.textContent = t("now.activity.empty");
    list.append(empty);
  } else {
    // Collapse CAP warning fragments (groupIncidents handles worsening-first +
    // nearest-distance ordering at the row level; singles render as before).
    for (const row of groupIncidents(status.inRadius)) {
      if (row.kind === "group") {
        list.append(groupCard(row.group, { t, nowMs: ctx.nowMs, showTicks: true, onOpen: ctx.onOpenIncident }));
        continue;
      }
      const { view, distanceKm } = row.item;
      const rowWrap = document.createElement("div");
      rowWrap.className = "now-activity__row";
      rowWrap.setAttribute("data-incident", view.subject);
      applyAgingAttrs(rowWrap, view, ctx.nowMs, t);
      const open = document.createElement("button");
      open.type = "button";
      open.className = "now-activity__open";
      open.setAttribute("data-open-incident", view.subject);
      open.append(
        hairlineRow({
          icon: hazardIcon(view.hazard),
          leading: t(`hazard.${view.hazard}`),
          trailing: t("now.activity.corroboration", { n: view.distinctIdentities }),
          tone: "accent",
        }),
      );
      if (ctx.onOpenIncident) open.addEventListener("click", () => ctx.onOpenIncident!(view.subject));
      rowWrap.append(open);
      // severity proxy: confidence → ticks (read-only display)
      rowWrap.append(severityTicks(Math.max(1, Math.round(view.confidence * 5))));
      list.append(rowWrap);
    }
  }
  root.append(list);

  // --- Raw reports (power-user opt-in) — newest-first, capped (the live tier floods) ---
  if (ctx.rawReports && ctx.rawReports.length > 0) {
    const raw = document.createElement("div");
    raw.className = "now-raw-list";
    const rh = document.createElement("div");
    rh.className = "bx-microlabel";
    rh.textContent = t("now.raw.list.title", { n: ctx.rawReports.length });
    raw.append(rh);
    const recent = [...ctx.rawReports].slice(-RAW_LIST_CAP).reverse();
    for (const r of recent) {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "now-raw-list__row";
      row.setAttribute("data-raw-report", r.id);
      // Category (via the hazard bucket label) + response type when present.
      const lead = r.responseType ? `${t(`hazard.${r.hazard}`)} · ${r.responseType}` : t(`hazard.${r.hazard}`);
      row.append(hairlineRow({ icon: hazardIcon(r.hazard), leading: lead, trailing: r.feed ?? "", tone: "ink" }));
      if (ctx.onOpenReport) row.addEventListener("click", () => ctx.onOpenReport!(r.id));
      raw.append(row);
    }
    if (ctx.rawReports.length > RAW_LIST_CAP) {
      const more = document.createElement("p");
      more.className = "bx-microlabel now-raw-list__more";
      more.textContent = t("now.raw.list.more", { n: ctx.rawReports.length - RAW_LIST_CAP });
      raw.append(more);
    }
    root.append(raw);
  }

  // "Other watched places" — saved-zone incidents that alerted once and now live here
  // (named, tap-to-open), kept distinct from the home/live verdict above.
  if (ctx.watched && ctx.watched.length > 0) {
    const watched = document.createElement("div");
    watched.className = "now-watched";
    const wh = document.createElement("div");
    wh.className = "bx-microlabel";
    wh.textContent = t("now.watched.title");
    watched.append(wh);
    for (const row of groupIncidents(ctx.watched)) {
      if (row.kind === "group") {
        watched.append(groupCard(row.group, { t, nowMs: ctx.nowMs, showTicks: false, onOpen: ctx.onOpenIncident }));
        continue;
      }
      const { view, distanceKm, zoneName } = row.item;
      const rowWrap = document.createElement("div");
      rowWrap.className = "now-activity__row";
      rowWrap.setAttribute("data-watched", view.subject);
      applyAgingAttrs(rowWrap, view, ctx.nowMs, t);
      const open = document.createElement("button");
      open.type = "button";
      open.className = "now-activity__open";
      open.setAttribute("data-open-incident", view.subject);
      open.append(hairlineRow({
        icon: hazardIcon(view.hazard),
        leading: t(`hazard.${view.hazard}`),
        ugc: zoneName ?? t("you.loc.placeUnnamed"),
        trailing: t("you.loc.km", { n: distanceKm.toFixed(1) }),
      }));
      if (ctx.onOpenIncident) open.addEventListener("click", () => ctx.onOpenIncident!(view.subject));
      rowWrap.append(open);
      watched.append(rowWrap);
    }
    root.append(watched);
  }

  // --- Camera toggle FAB — one control cycling follow ⇄ fit (icon shows the CURRENT mode):
  //   follow → locate (active), tap frames all; fit → frame glyph, tap re-follows;
  //   free (panned) → locate (inactive), tap re-centers. Without a live position only the
  //   fit affordance is meaningful (needs ≥1 monitored place — home alone counts). ---
  // It lives ON the map (top-right corner), so it reads as a map control and stays clear
  // of the Report FAB at the bottom-right — consistently on mobile + desktop. The map
  // element persists across re-renders, so clear any prior toggle before re-adding.
  mapEl.querySelectorAll(".camera-fab").forEach((c) => c.remove());
  // Any monitored place (even a lone home zone) is worth a frame affordance: once the
  // user pans away there's otherwise no way back to their area. Uses the computed
  // `rings` (home fallback) so home-only callers still get the control.
  const hasFramablePlace = rings.some((r) => r.cell && r.kind !== "live");
  const mode = ctx.cameraMode ?? "follow";
  const addCamera = (cameraMode: "follow" | "fit" | "free", icon: "locate" | "frame", label: string, onClick: () => void) => {
    const cam = iconButton({ label, icon, variant: "secondary", onClick });
    cam.classList.add("camera-fab");
    cam.setAttribute("data-action", "camera");
    cam.setAttribute("data-camera-mode", cameraMode);
    if (cameraMode !== "free") cam.setAttribute("data-active", "");
    mapEl.append(cam);
  };
  if (ctx.live && (ctx.onRecenter || ctx.onFitAll)) {
    if (mode === "follow") {
      addCamera("follow", "locate", t("now.fitAll"), () => ctx.onFitAll?.());
    } else {
      // fit or free → tapping re-centers on the live position.
      addCamera(mode, mode === "fit" ? "frame" : "locate", t("now.recenter"), () => ctx.onRecenter?.());
    }
  } else if (ctx.onFitAll && hasFramablePlace) {
    addCamera("fit", "frame", t("now.fitAll"), () => ctx.onFitAll!());
  }

  // --- Report button (C3: opens the live report modal) — SVG hazard glyph FAB ---
  const report = iconButton({
    label: t("now.report.button"),
    icon: "report",
    variant: "primary",
    onClick: () => {
      if (root.querySelector("[data-sheet=report]")) return; // already open
      // Per-open picker map: torn down on close so repeated opens don't leak WebGL contexts.
      let pickerMap: MapHandle | undefined;
      let sheet: HTMLElement;
      const rawClose = (): void => { pickerMap?.destroy(); sheet.remove(); };
      const close = ctx.onModalBack ? ctx.onModalBack(rawClose) : rawClose;
      sheet = reportSheet({
        t,
        homeCell: home.cell || "",
        placeName: home.placeName,
        nearby: status.inRadius.map((x) => x.view),
        geolocate: ctx.geolocate,
        currentCell: ctx.live ? latLngToCell(ctx.live.lat, ctx.live.lng, 8) : undefined,
        homePlaceName: home.placeName,
        // Precise placement: pan the map under the center pin; the res-8 cell beneath
        // it becomes the report location (WebGL-guarded → inert in tests/degraded).
        mountPicker: (container, onPick) => {
          pickerMap = mountPaperMap(container, {
            centerCell: home.cell || undefined,
            centerPin: true,
            onCenterCell: onPick,
          });
        },
        onSubmit: ctx.onReport ?? (async () => {}),
        onClose: close,
      });
      root.append(sheet);
    },
  });
  report.classList.add("report-fab");
  report.setAttribute("data-action", "report");
  root.append(report);

  return root;
}
